/**
 * Trust checks for separately hosted Foundation OS userlands.
 *
 * Userland deep-links to kernel `/entrar` with `returnTo=<app origin><path>`.
 * After sign-in we bounce through `/api/auth/web-return` so the session cookie
 * can be minted host-only on the userland origin (Portless: foundation-web /
 * foundation-oficina ≠ foundation).
 *
 * A trusted origin receives a ticket that carries the session token, and is
 * accepted by Better Auth's origin check, so trust is by exact origin:
 *
 * - Explicit `*_URL` env origins: every runtime. Retired userland hosts are
 *   never trusted by default.
 * - {@link USERLAND_DEV_ORIGINS_ENV}: exact local userland origins, every
 *   runtime. Empty by default. The only way a production or preview kernel
 *   trusts a local userland app.
 * - Development runtime only (`isDevelopmentRuntime()`, a `next dev` / Vitest
 *   process; never production or preview, on any host): the Portless siblings
 *   of this process's own `PORTLESS_URL` (exact origins, any TLD), and any
 *   Portless userland host under the reserved `.test` / `.localhost` TLDs.
 *
 * `.dev` is a public TLD: nobody owns `foundation-web.dev` just because
 * Portless maps it in /etc/hosts. It is never trusted by pattern, in any
 * runtime.
 */

import { PILOT_OPERATOR_OAUTH_CLIENT_ID } from "./platform-oauth-admission";
import { isSafeReturnPath } from "@garagem/foundation-shared/lib/auth/safe-return-path";
type UserlandKind = "web" | "oficina";

const USERLAND_KINDS: readonly UserlandKind[] = ["web", "oficina"];

/**
 * Exact origins of local userland apps this kernel trusts, comma or
 * whitespace separated (`https://foundation-web.test,https://…`). Runtime
 * read, server only. Wildcards are ignored, and so is any entry whose host is
 * not a Portless userland host (`[<branch>.]foundation-web|oficina.<tld>`).
 * Prefer `.test` / `.localhost` names: an origin under a public TLD is only as
 * safe as the ownership of that domain. Docs:
 * apps/foundation/docs/infrastructure/infisical.md.
 */
export const USERLAND_DEV_ORIGINS_ENV = "FOUNDATION_USERLAND_DEV_ORIGINS";

/** `[<branch>.]foundation-<kind>.<tld>`: Portless host of a userland app. */
const PORTLESS_USERLAND_HOST =
  /^(?:[a-z0-9-]+\.)*foundation-(web|oficina)\.([a-z0-9-]+)$/i;

const RETIRED_ADMIN_HOST = "admin.garagem.ai";
const PORTLESS_ADMIN_HOST = /^(?:[a-z0-9-]+\.)*foundation-admin\.[a-z0-9-]+$/i;

function isRetiredAdminHost(hostname: string): boolean {
  return (
    hostname.toLowerCase() === RETIRED_ADMIN_HOST ||
    PORTLESS_ADMIN_HOST.test(hostname)
  );
}

function isRetiredAdminOrigin(origin: string): boolean {
  try {
    return isRetiredAdminHost(new URL(origin).hostname);
  } catch {
    return false;
  }
}

/** `[<branch>.]foundation.<tld>`: Portless host of the kernel itself. */
const PORTLESS_KERNEL_HOST = /^((?:[a-z0-9-]+\.)*)foundation\.([a-z0-9-]+)$/i;

/** RFC 6761 reserved TLDs: never delegated, so nobody else can own a name. */
const RESERVED_DEV_TLDS: readonly string[] = ["test", "localhost"];

/** The userland app and TLD a Portless-shaped hostname names, or null. */
function portlessUserlandHost(
  hostname: string
): { kind: UserlandKind; tld: string } | null {
  const match = PORTLESS_USERLAND_HOST.exec(hostname);
  if (!match) return null;
  return {
    kind: match[1]!.toLowerCase() as UserlandKind,
    tld: match[2]!.toLowerCase(),
  };
}

const OFICINA_WEB_RETURN_PATH = "/oficina/api/auth/web-return";
const USERLAND_WEB_RETURN_PATH = "/api/auth/web-return";
export interface TrustedWebOriginHost {
  envSource(): Readonly<Record<string, string | undefined>>;
  isDevelopmentRuntime(): boolean;
  parseOriginList(raw: string | undefined | null): string[];
  getFoundationAllowedOrigins(defaultBaseUrl: string): string[];
}

function createUserlandTrustPolicy(runtime: TrustedWebOriginHost) {
  // Bracket access: a `process.env.X` member expression may be replaced at
  // build time; a variable key is always a runtime read (unset in the browser).
  function readEnv(key: string): string | undefined {
    return runtime.envSource()[key];
  }

  /** {@link USERLAND_DEV_ORIGINS_ENV} entries by origin. Any runtime. */
  function explicitUserlandDevOrigins(): Map<string, UserlandKind> {
    const out = new Map<string, UserlandKind>();
    for (const origin of runtime.parseOriginList(
      readEnv(USERLAND_DEV_ORIGINS_ENV)
    )) {
      if (origin.includes("*")) continue;
      let hostname: string;
      try {
        hostname = new URL(origin).hostname;
      } catch {
        continue;
      }
      const host = portlessUserlandHost(hostname);
      if (host) out.set(origin, host.kind);
    }
    return out;
  }

  /**
   * The userland siblings of this process's own Portless URL
   * (`https://feat-x.foundation.dev` -> `https://feat-x.foundation-web.dev`).
   * `PORTLESS_URL` is set by the wrapping `portless` process, never by a
   * request. Development runtime only.
   */
  function portlessSiblingOrigins(): Map<string, UserlandKind> {
    const out = new Map<string, UserlandKind>();
    if (!runtime.isDevelopmentRuntime()) return out;
    let self: URL;
    try {
      self = new URL(readEnv("PORTLESS_URL") ?? "");
    } catch {
      return out;
    }
    const match = PORTLESS_KERNEL_HOST.exec(self.hostname);
    if (!match) return out;
    const port = self.port ? `:${self.port}` : "";
    for (const kind of USERLAND_KINDS) {
      out.set(
        `${self.protocol}//${match[1]}foundation-${kind}.${match[2]}${port}`.toLowerCase(),
        kind
      );
    }
    return out;
  }

  /**
   * Which local userland app `url` is, when this runtime trusts it: an explicit
   * {@link USERLAND_DEV_ORIGINS_ENV} entry (any runtime), or, on the development
   * runtime only, a sibling of this process's Portless URL or a Portless
   * userland host under a reserved TLD.
   */
  function trustedDevUserlandKind(url: URL): UserlandKind | null {
    const explicit = explicitUserlandDevOrigins().get(url.origin);
    if (explicit) return explicit;
    if (!runtime.isDevelopmentRuntime()) return null;
    const sibling = portlessSiblingOrigins().get(url.origin);
    if (sibling) return sibling;
    const host = portlessUserlandHost(url.hostname);
    if (host && RESERVED_DEV_TLDS.includes(host.tld)) return host.kind;
    return null;
  }

  /** Local userland origins for Better Auth `trustedOrigins`. Same rules. */
  function devUserlandTrustedOrigins(): string[] {
    const out = [...explicitUserlandDevOrigins().keys()];
    if (!runtime.isDevelopmentRuntime()) return out;
    out.push(...portlessSiblingOrigins().keys());
    for (const kind of USERLAND_KINDS) {
      for (const tld of RESERVED_DEV_TLDS) {
        out.push(`https://foundation-${kind}.${tld}`);
        out.push(`https://*.foundation-${kind}.${tld}`);
      }
    }
    return out;
  }

  /** Redeem path on the Oficina origin — Next `basePath: "/oficina"`. */

  /** Redeem path on web origins (no basePath). */

  /**
   * Better Auth `trustedOrigins` for CSRF on proxied userland BFF POSTs.
   * Includes every Foundation host on every stack (app, os, canary,
   * `FOUNDATION_ALLOWED_ORIGINS`, dev hosts outside production — read at
   * runtime, not from the baked `NEXT_PUBLIC_URL`), and the local userland origins this runtime
   * trusts (module doc): `FOUNDATION_USERLAND_DEV_ORIGINS` everywhere; Portless
   * siblings and `.test` / `.localhost` wildcards on the development runtime
   * only. Never a `.dev` wildcard. Callers pass `config.auth.baseUrl`.
   */
  function buildBetterAuthTrustedOrigins(authBaseUrl: string): string[] {
    const origins = [
      authBaseUrl,
      ...runtime.getFoundationAllowedOrigins(authBaseUrl),
      "https://claude.ai",
      ...devUserlandTrustedOrigins(),
      ...originsFromEnv([
        "NEXT_PUBLIC_WEB_URL",
        "WEB_URL",
        "NEXT_PUBLIC_OFICINA_URL",
        "OFICINA_URL",
      ]),
    ];
    return [...new Set(origins)].filter(
      (origin) => !isRetiredAdminOrigin(origin)
    );
  }

  function originsFromEnv(keys: readonly string[]): string[] {
    const out: string[] = [];
    for (const key of keys) {
      const raw = runtime.envSource()[key]?.trim();
      if (!raw) continue;
      try {
        out.push(new URL(raw).origin);
      } catch {
        /* ignore */
      }
    }
    return out;
  }

  function userlandOriginFromEnv(): string[] {
    return originsFromEnv([
      "NEXT_PUBLIC_WEB_URL",
      "WEB_URL",
      "NEXT_PUBLIC_OFICINA_URL",
      "OFICINA_URL",
    ]);
  }

  function oficinaOriginFromEnv(): string[] {
    return originsFromEnv(["NEXT_PUBLIC_OFICINA_URL", "OFICINA_URL"]);
  }

  return {
    buildBetterAuthTrustedOrigins,
    trustedDevUserlandKind,
    userlandOriginFromEnv,
    oficinaOriginFromEnv,
  };
}

export function createTrustedWebOriginPolicy(runtime: TrustedWebOriginHost) {
  const {
    buildBetterAuthTrustedOrigins,
    trustedDevUserlandKind,
    userlandOriginFromEnv,
    oficinaOriginFromEnv,
  } = createUserlandTrustPolicy(runtime);
  /** A compiled preview needs a host-only session cookie on its own origin. */
  function shareGaragemAuthCookies(
    env: Readonly<Record<string, string | undefined>> = runtime.envSource()
  ): boolean {
    return (
      env.NODE_ENV === "production" &&
      env.GARAGEM_RUNTIME_ENV?.trim().toLowerCase() !== "preview"
    );
  }

  function isTrustedWebOrigin(origin: string): boolean {
    let url: URL;
    try {
      url = new URL(origin);
    } catch {
      return false;
    }

    if (url.protocol !== "https:" && url.protocol !== "http:") return false;

    if (isRetiredAdminHost(url.hostname)) return false;

    if (trustedDevUserlandKind(url) !== null) return true;

    if (userlandOriginFromEnv().includes(url.origin)) return true;

    return false;
  }

  /** Oficina app origins — BFF lives under Next `basePath: "/oficina"`. */
  function isTrustedOficinaOrigin(origin: string): boolean {
    let url: URL;
    try {
      url = new URL(origin);
    } catch {
      return false;
    }

    if (url.protocol !== "https:" && url.protocol !== "http:") return false;

    if (isRetiredAdminHost(url.hostname)) return false;

    if (trustedDevUserlandKind(url) === "oficina") return true;

    if (oficinaOriginFromEnv().includes(url.origin)) return true;

    return false;
  }

  /**
   * Path on the userland origin that redeems the web-return ticket.
   * Oficina mounts the handler under `/oficina`; web does not.
   */
  function userlandWebReturnPath(origin: string): string {
    return isTrustedOficinaOrigin(origin)
      ? OFICINA_WEB_RETURN_PATH
      : USERLAND_WEB_RETURN_PATH;
  }

  /** The retired Admin BFF cannot initiate kernel handoffs. */
  function isTrustedAdminOrigin(_origin: string): boolean {
    return false;
  }

  /**
   * Absolute https/http URL on a trusted web origin with a safe path.
   * Rejects open redirects to arbitrary hosts.
   */
  function isTrustedWebReturnUrl(value: unknown): value is string {
    return isTrustedReturnUrl(value, isTrustedWebOrigin);
  }

  /**
   * Retired Admin handoff return targets are never trusted.
   */
  function isTrustedAdminReturnUrl(value: unknown): value is string {
    return isTrustedReturnUrl(value, isTrustedAdminOrigin);
  }

  function isTrustedReturnUrl(
    value: unknown,
    isTrustedOrigin: (origin: string) => boolean
  ): value is string {
    if (typeof value !== "string" || value.length === 0) return false;

    let url: URL;
    try {
      url = new URL(value);
    } catch {
      return false;
    }

    if (url.protocol !== "https:" && url.protocol !== "http:") return false;
    if (!isTrustedOrigin(url.origin)) return false;

    const path = `${url.pathname}${url.search}`;
    return isSafeReturnPath(path);
  }

  /** Same-origin bridge path consumed by authRedirect / OAuth callbackURL. */
  function webReturnBridgePath(returnTo: string): string {
    return `/api/auth/web-return?returnTo=${encodeURIComponent(returnTo)}`;
  }

  /**
   * Whether the browser should send `returnTo` through the same-origin
   * `/api/auth/web-return` bridge. Not a trust decision: the bridge route
   * re-checks {@link isTrustedWebReturnUrl} on the server and answers 400 with
   * no ticket for anything it does not trust.
   *
   * The browser cannot see `PORTLESS_URL` or
   * {@link USERLAND_DEV_ORIGINS_ENV}, so a Portless-shaped userland URL is
   * handed to the server to decide instead of being dropped here.
   */
  function shouldUseWebReturnBridge(value: unknown): value is string {
    if (isTrustedWebReturnUrl(value)) return true;
    if (typeof value !== "string" || value.length === 0) return false;
    let url: URL;
    try {
      url = new URL(value);
    } catch {
      return false;
    }
    if (url.protocol !== "https:" && url.protocol !== "http:") return false;
    if (!portlessUserlandHost(url.hostname)) return false;
    return isSafeReturnPath(`${url.pathname}${url.search}`);
  }

  /**
   * Whether kernel /entrar should show operator Google SSO.
   *
   * Exactly two triggers: the explicit `?admin=true` parameter, and the Pilot
   * operator OAuth flow. A `from=/admin…` return path no longer counts (the
   * `/admin` UI path was retired, DL-025).
   */
  function shouldShowOperatorGoogleLogin(
    searchParams: URLSearchParams | { get(name: string): string | null }
  ): boolean {
    if (searchParams.get("admin") === "true") return true;
    // Pilot's unauthenticated authorize bounce lands on /entrar with this
    // client id. Proxy also adds admin=true; this covers a request that
    // already carries the OAuth query.
    if (searchParams.get("client_id") === PILOT_OPERATOR_OAUTH_CLIENT_ID) {
      return true;
    }

    return false;
  }
  return {
    shareGaragemAuthCookies,
    buildBetterAuthTrustedOrigins,
    isTrustedWebOrigin,
    isTrustedOficinaOrigin,
    userlandWebReturnPath,
    isTrustedAdminOrigin,
    isTrustedWebReturnUrl,
    isTrustedAdminReturnUrl,
    webReturnBridgePath,
    shouldUseWebReturnBridge,
    shouldShowOperatorGoogleLogin,
  };
}
